import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { parseTag, tagOf } from "@/lib/taxonomy";

function toSelection(tags) {
  const sel = {};
  for (const tag of tags) {
    const p = parseTag(tag);
    if (p) (sel[p.key] ??= []).push(p.value);
  }
  return sel;
}

// মডারেটর পেপারের ট্যাগ ঠিক করে। taxonomies = সব ক্যাটাগরি (নিষ্ক্রিয় সহ); একক-বাছাইয়ে select, একাধিকে chip।
// পেপারে আগে থেকে থাকা নিষ্ক্রিয় মান দেখানো হয় (সরানো যায়, রেখে দেওয়াও যায়)। যে ক্যাটাগরি এখন অজানা/নিষ্ক্রিয়,
// তার ট্যাগে হাত পড়ে না — যেমন আছে তেমনই থাকে।
export default function TagEditor({ taxonomies, tags, onSave }) {
  const [selection, setSelection] = useState(() => toSelection(tags));
  const [saving, setSaving] = useState(false);

  const editable = taxonomies.filter((x) => x.active);
  const editableKeys = new Set(editable.map((x) => x.key));
  const kept = tags.filter((t) => {
    const p = parseTag(t);
    return !p || !editableKeys.has(p.key);
  });
  const next = [...kept, ...Object.entries(selection).flatMap(([k, vs]) => (editableKeys.has(k) ? vs.map((v) => tagOf(k, v)) : []))];
  const dirty = [...next].sort().join("|") !== [...tags].sort().join("|");

  const setOne = (key, value) => setSelection((s) => ({ ...s, [key]: value ? [value] : [] }));
  const toggle = (key, value) =>
    setSelection((s) => {
      const cur = s[key] ?? [];
      return { ...s, [key]: cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value] };
    });

  const save = async () => {
    setSaving(true);
    try {
      await onSave(next);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3 pt-2">
      {editable.map((x) => {
        const chosen = selection[x.key] ?? [];
        const terms = x.terms.filter((m) => m.active || chosen.includes(m.value));
        return (
          <div key={x.key} className="space-y-1">
            <p className="text-xs text-muted-foreground">{x.label_bn}</p>
            {x.multi ? (
              <div className="flex flex-wrap gap-1.5">
                {terms.map((m) => {
                  const on = chosen.includes(m.value);
                  return (
                    <button
                      key={m.value}
                      type="button"
                      aria-pressed={on}
                      onClick={() => toggle(x.key, m.value)}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                        on ? "bg-primary text-primary-foreground border-primary" : "border-input text-muted-foreground"
                      }`}
                    >
                      {m.label_bn}
                    </button>
                  );
                })}
              </div>
            ) : (
              <select
                value={chosen[0] ?? ""}
                onChange={(e) => setOne(x.key, e.target.value)}
                className="h-9 w-full rounded-md border border-input bg-transparent px-2.5 text-sm text-foreground"
              >
                <option value="">—</option>
                {terms.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label_bn}
                    {m.active ? "" : " (নিষ্ক্রিয়)"}
                  </option>
                ))}
              </select>
            )}
          </div>
        );
      })}
      <Button size="sm" disabled={!dirty || saving} onClick={save}>
        {saving && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
        ট্যাগ সেভ করো
      </Button>
    </div>
  );
}
